// SMSF Lite Mobile - Top 15 Essential Tools
// Mobile-optimized SMSF calculator suite
// Uses existing SMSF calculation engines from desktop

import React, { useState, useRef } from 'react';
import { SMSFCoreEngines, SMSFTaxEngine, SMSFComplianceEngine } from '../SMSFCoreEngines';
import { FinancialConfig, calculateDivision293Detail } from '../FinancialConfig';
import SMSF_RATES_CONFIG from '../SMSF_Rates_Config';
import { EmailCaptureForm } from './EmailCaptureForm';

export const SMSFLiteMobile = ({ onBack }) => {
    const [activeTool, setActiveTool] = useState(null); // null = home, number = tool index
    const [isScrolled, setIsScrolled] = useState(false);
    
    // Track scroll for sticky header
    React.useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.pageYOffset > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    
    // 15 Essential SMSF Tools
    const tools = [
        {
            id: 'contribution-caps',
            name: 'Contribution Caps',
            icon: '💰',
            description: 'Track concessional & non-concessional contribution limits',
            color: 'blue'
        },
        {
            id: 'division-293',
            name: 'Division 293 Tax',
            icon: '💵',
            description: 'Calculate additional 15% tax on high earners',
            color: 'red'
        },
        {
            id: 'lrba',
            name: 'LRBA Calculator',
            icon: '🏛️',
            description: 'Limited Recourse Borrowing Arrangement analysis',
            color: 'purple'
        },
        {
            id: 'pension-optimizer',
            name: 'Pension Optimizer',
            icon: '💰',
            description: 'Optimize pension drawdowns and TBC usage',
            color: 'green'
        },
        {
            id: 'carry-forward',
            name: 'Carry Forward',
            icon: '📅',
            description: 'Unused concessional cap carry-forward calculator',
            color: 'indigo'
        },
        {
            id: 'fee-analysis',
            name: 'Fee Analysis',
            icon: '💳',
            description: 'Compare SMSF vs retail/industry super fees',
            color: 'orange'
        },
        {
            id: 'cash-flow',
            name: 'Cash Flow',
            icon: '💹',
            description: 'Model SMSF income, expenses, and liquidity',
            color: 'teal'
        },
        {
            id: 'compliance',
            name: 'Compliance Check',
            icon: '✅',
            description: 'Ensure SMSF meets all regulatory requirements',
            color: 'green'
        },
        {
            id: 'tbc-tracking',
            name: 'TBC Tracking',
            icon: '📊',
            description: 'Transfer Balance Cap monitoring',
            color: 'blue'
        },
        {
            id: 'investment-strategy',
            name: 'Investment Strategy',
            icon: '📈',
            description: 'Asset allocation and diversification guidance',
            color: 'purple'
        },
        {
            id: 'downsizer',
            name: 'Downsizer',
            icon: '🏠',
            description: 'Downsizer contribution calculator (age 55+)',
            color: 'sky'
        },
        {
            id: 'co-contribution',
            name: 'Gov Co-Contribution',
            icon: '💚',
            description: 'Calculate government co-contribution eligibility',
            color: 'green'
        },
        {
            id: 'tax-harvesting',
            name: 'Tax Loss Harvesting',
            icon: '📉',
            description: 'Optimize capital gains and losses',
            color: 'red'
        },
        {
            id: 'work-test',
            name: 'Work Test',
            icon: '⏱️',
            description: 'Age 67-74 contribution work test calculator',
            color: 'yellow'
        },
        {
            id: 'spouse-split',
            name: 'Spouse Splitting',
            icon: '💑',
            description: 'Contribution splitting strategies for couples',
            color: 'pink'
        }
    ];
    
    // Tool Selection Screen
    if (activeTool === null) {
        return (
            <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '20px' }}>
                {/* Header */}
                <div className={`bg-green-600 text-white px-3 sticky top-0 z-20 shadow-md transition-all ${
                    isScrolled ? 'py-2' : 'py-3'
                }`}
                     style={{ paddingTop: isScrolled ? '8px' : 'max(env(safe-area-inset-top), 12px)' }}>
                    <div className="flex items-center justify-between">
                        <button onClick={onBack} className="p-1 active:bg-green-700 rounded">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h1 className={`font-bold transition-all ${isScrolled ? 'text-sm' : 'text-base'}`}>
                            SMSF Lite
                        </h1>
                        <div className="w-7"></div>
                    </div>
                </div>
                
                {/* Tool Grid */}
                <div className="p-4">
                    <div className="bg-white rounded-2xl p-6 shadow-lg mb-4">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">15 Essential Tools</h2>
                        <p className="text-sm text-gray-600">Mobile-optimized SMSF calculators</p>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-3">
                        {tools.map((tool, index) => (
                            <button
                                key={tool.id}
                                onClick={() => setActiveTool(index)}
                                className="bg-white rounded-xl p-4 shadow-md active:shadow-lg active:scale-98 transition text-left"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="text-3xl">{tool.icon}</div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-900 text-base">{tool.name}</h3>
                                        <p className="text-sm text-gray-600 mt-1">{tool.description}</p>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </button>
                        ))}
                    </div>
                    {/* Email capture - SMSF insights */}
                    <div className="mt-6">
                        <EmailCaptureForm calculatorType="smsf" results={null} inputs={null} getPdfBase64={null} />
                    </div>
                </div>
            </div>
        );
    }
    
    // Individual Tool Views
    const tool = tools[activeTool];
    
    // Render specific tool calculator
    switch (tool.id) {
        case 'contribution-caps':
            return <ContributionCapsCalculator onBack={() => setActiveTool(null)} />;
        case 'division-293':
            return <Division293Calculator onBack={() => setActiveTool(null)} />;
        case 'lrba':
            return <LRBACalculator onBack={() => setActiveTool(null)} />;
        case 'pension-optimizer':
            return <PensionOptimizerCalculator onBack={() => setActiveTool(null)} />;
        case 'carry-forward':
            return <CarryForwardCalculator onBack={() => setActiveTool(null)} />;
        case 'fee-analysis':
            return <FeeAnalysisCalculator onBack={() => setActiveTool(null)} />;
        case 'cash-flow':
            return <CashFlowCalculator onBack={() => setActiveTool(null)} />;
        case 'compliance':
            return <ComplianceCalculator onBack={() => setActiveTool(null)} />;
        case 'tbc-tracking':
            return <TBCTrackingCalculator onBack={() => setActiveTool(null)} />;
        case 'investment-strategy':
            return <InvestmentStrategyCalculator onBack={() => setActiveTool(null)} />;
        case 'downsizer':
            return <DownsizerCalculator onBack={() => setActiveTool(null)} />;
        case 'co-contribution':
            return <CoContributionCalculator onBack={() => setActiveTool(null)} />;
        case 'tax-harvesting':
            return <TaxHarvestingCalculator onBack={() => setActiveTool(null)} />;
        case 'work-test':
            return <WorkTestCalculator onBack={() => setActiveTool(null)} />;
        case 'spouse-split':
            return <SpouseSplitCalculator onBack={() => setActiveTool(null)} />;
        default:
            return <ComingSoonTool tool={tool} onBack={() => setActiveTool(null)} />;
    }
};

// Individual Calculator Components

// 1. Contribution Caps Calculator
const ContributionCapsCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        age: 50,
        totalSuperBalance: 500000,
        concessionalUsed: 0,
        nonConcessionalUsed: 0,
        newConcessional: 0,
        newNonConcessional: 0
    });
    
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const caps = FinancialConfig.CONTRIBUTION_CAPS;
        const concessionalCap = caps.CONCESSIONAL;
        const nonConcessionalCap = caps.NON_CONCESSIONAL;
        const tsbThreshold = caps.TOTAL_BALANCE_THRESHOLD;
        
        const isOverTSB = inputs.totalSuperBalance >= tsbThreshold;
        const totalConcessional = inputs.concessionalUsed + inputs.newConcessional;
        const totalNonConcessional = inputs.nonConcessionalUsed + inputs.newNonConcessional;
        
        const concessionalRemaining = Math.max(0, concessionalCap - totalConcessional);
        const concessionalOver = Math.max(0, totalConcessional - concessionalCap);
        
        const nonConcessionalRemaining = isOverTSB ? 0 : Math.max(0, nonConcessionalCap - totalNonConcessional);
        const nonConcessionalOver = Math.max(0, totalNonConcessional - nonConcessionalCap);
        
        setResults({
            concessional: {
                cap: concessionalCap,
                used: totalConcessional,
                remaining: concessionalRemaining,
                over: concessionalOver,
                compliant: concessionalOver === 0
            },
            nonConcessional: {
                cap: isOverTSB ? 0 : nonConcessionalCap,
                used: totalNonConcessional,
                remaining: nonConcessionalRemaining,
                over: nonConcessionalOver,
                compliant: nonConcessionalOver === 0,
                blocked: isOverTSB
            },
            totalSuperBalance: inputs.totalSuperBalance,
            tsbThreshold
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-blue-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-blue-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Contribution Caps</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                {/* Inputs */}
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Your Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Age</label>
                        <input
                            type="number"
                            value={inputs.age}
                            onChange={(e) => setInputs(prev => ({ ...prev, age: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Total Super Balance ($)</label>
                        <input
                            type="number"
                            value={inputs.totalSuperBalance}
                            onChange={(e) => setInputs(prev => ({ ...prev, totalSuperBalance: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Concessional Used This Year ($)</label>
                        <input
                            type="number"
                            value={inputs.concessionalUsed}
                            onChange={(e) => setInputs(prev => ({ ...prev, concessionalUsed: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Non-Concessional Used This Year ($)</label>
                        <input
                            type="number"
                            value={inputs.nonConcessionalUsed}
                            onChange={(e) => setInputs(prev => ({ ...prev, nonConcessionalUsed: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl"
                        />
                    </div>
                </div>
                
                {/* Results */}
                {results && (
                    <div className="space-y-3">
                        {/* Concessional */}
                        <div className={`rounded-xl p-6 ${results.concessional.compliant ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                            <h3 className="font-bold text-gray-900 mb-3">Concessional Contributions</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Annual Cap:</span>
                                    <span className="font-bold">${results.concessional.cap.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Used:</span>
                                    <span className="font-bold">${results.concessional.used.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Remaining:</span>
                                    <span className="font-bold text-green-600">${results.concessional.remaining.toLocaleString()}</span>
                                </div>
                                {results.concessional.over > 0 && (
                                    <div className="flex justify-between text-red-600">
                                        <span>Over Limit:</span>
                                        <span className="font-bold">${results.concessional.over.toLocaleString()}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Non-Concessional */}
                        <div className={`rounded-xl p-6 ${results.nonConcessional.compliant ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                            <h3 className="font-bold text-gray-900 mb-3">Non-Concessional Contributions</h3>
                            {results.nonConcessional.blocked ? (
                                <div className="bg-red-100 p-3 rounded-lg">
                                    <p className="text-sm text-red-800 font-semibold">❌ Blocked</p>
                                    <p className="text-xs text-red-700 mt-1">
                                        Total Super Balance ≥ ${results.tsbThreshold.toLocaleString()}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>Annual Cap:</span>
                                        <span className="font-bold">${results.nonConcessional.cap.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Used:</span>
                                        <span className="font-bold">${results.nonConcessional.used.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Remaining:</span>
                                        <span className="font-bold text-green-600">${results.nonConcessional.remaining.toLocaleString()}</span>
                                    </div>
                                    {results.nonConcessional.over > 0 && (
                                        <div className="flex justify-between text-red-600">
                                            <span>Over Limit:</span>
                                            <span className="font-bold">${results.nonConcessional.over.toLocaleString()}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            
            {/* Calculate Button */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button
                    onClick={handleCalculate}
                    className="w-full py-4 bg-blue-600 text-white font-bold text-base rounded-xl active:bg-blue-700"
                >
                    Calculate Caps
                </button>
            </div>
        </div>
    );
};

// 2. Division 293 Tax Calculator
const Division293Calculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        assessableIncome: 250000,
        reportableFringeBenefits: 0,
        reportableEmployerSuper: 0,
        concessionalContributions: 27500,
        netInvestmentLoss: 0
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const incomeForSurcharge = inputs.assessableIncome + inputs.reportableFringeBenefits
            + inputs.reportableEmployerSuper - inputs.netInvestmentLoss;
        const detail = calculateDivision293Detail(incomeForSurcharge, inputs.concessionalContributions);
        setResults({
            incomeForSurcharge: detail.incomeForSurcharge,
            threshold: FinancialConfig.DIVISION_293.INCOME_THRESHOLD,
            excessIncome: detail.excessIncome,
            taxableAmount: detail.taxableAmount,
            div293Tax: detail.div293Tax,
            isLiable: detail.div293Tax > 0,
            effectiveRate: detail.effectiveTaxRate,
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-red-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-red-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Division 293 Tax</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Income Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Assessable Income ($)</label>
                        <input type="number" value={inputs.assessableIncome}
                            onChange={(e) => setInputs(prev => ({ ...prev, assessableIncome: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Reportable Fringe Benefits ($)</label>
                        <input type="number" value={inputs.reportableFringeBenefits}
                            onChange={(e) => setInputs(prev => ({ ...prev, reportableFringeBenefits: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Concessional Contributions ($)</label>
                        <input type="number" value={inputs.concessionalContributions}
                            onChange={(e) => setInputs(prev => ({ ...prev, concessionalContributions: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className="space-y-3">
                        <div className={`rounded-xl p-6 ${results.isLiable ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50 border-2 border-green-200'}`}>
                            <h3 className="font-bold text-gray-900 mb-3">Division 293 Tax Liability</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Income for Surcharge:</span>
                                    <span className="font-bold">${results.incomeForSurcharge.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Threshold:</span>
                                    <span className="font-bold">${results.threshold.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Excess Income:</span>
                                    <span className="font-bold">${results.excessIncome.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Taxable Amount:</span>
                                    <span className="font-bold">${results.taxableAmount.toLocaleString()}</span>
                                </div>
                                <div className="border-t-2 border-gray-300 pt-2 mt-2"></div>
                                <div className="flex justify-between text-lg">
                                    <span className="font-bold">Division 293 Tax:</span>
                                    <span className={`font-bold ${results.isLiable ? 'text-red-600' : 'text-green-600'}`}>
                                        ${results.div293Tax.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-red-600 text-white font-bold text-base rounded-xl active:bg-red-700">
                    Calculate Tax
                </button>
            </div>
        </div>
    );
};

// 3. LRBA Calculator
const LRBACalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        propertyValue: 500000,
        loanAmount: 400000,
        interestRate: 6.5,
        loanTerm: 25,
        rentalIncome: 24000,
        propertyExpenses: 8000
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const maxLVR = FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TO_VALUE;
        const lvr = (inputs.loanAmount / inputs.propertyValue) * 100;
        const equity = inputs.propertyValue - inputs.loanAmount;
        
        const monthlyRate = inputs.interestRate / 100 / 12;
        const totalPayments = inputs.loanTerm * 12;
        const monthlyPayment = inputs.loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / 
                              (Math.pow(1 + monthlyRate, totalPayments) - 1);
        const annualPayment = monthlyPayment * 12;
        const annualCashFlow = inputs.rentalIncome - inputs.propertyExpenses - annualPayment;
        const rentalYield = (inputs.rentalIncome / inputs.propertyValue) * 100;
        
        setResults({
            lvr,
            maxLVR: maxLVR * 100,
            isCompliant: lvr <= (maxLVR * 100),
            equity,
            monthlyPayment,
            annualPayment,
            annualCashFlow,
            cashFlowPositive: annualCashFlow >= 0,
            rentalYield
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-purple-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-purple-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">LRBA Calculator</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Property & Loan Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Property Value ($)</label>
                        <input type="number" value={inputs.propertyValue}
                            onChange={(e) => setInputs(prev => ({ ...prev, propertyValue: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Amount ($)</label>
                        <input type="number" value={inputs.loanAmount}
                            onChange={(e) => setInputs(prev => ({ ...prev, loanAmount: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Interest Rate (%)</label>
                        <input type="number" step="0.1" value={inputs.interestRate}
                            onChange={(e) => setInputs(prev => ({ ...prev, interestRate: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Term (years)</label>
                        <input type="number" value={inputs.loanTerm}
                            onChange={(e) => setInputs(prev => ({ ...prev, loanTerm: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Rental Income ($)</label>
                        <input type="number" value={inputs.rentalIncome}
                            onChange={(e) => setInputs(prev => ({ ...prev, rentalIncome: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className="space-y-3">
                        <div className={`rounded-xl p-6 ${results.isCompliant ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                            <h3 className="font-bold text-gray-900 mb-3">LRBA Analysis</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>LVR:</span>
                                    <span className="font-bold">{results.lvr.toFixed(2)}%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Max LVR:</span>
                                    <span className="font-bold">{results.maxLVR}%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Compliant:</span>
                                    <span className={`font-bold ${results.isCompliant ? 'text-green-600' : 'text-red-600'}`}>
                                        {results.isCompliant ? '✓ Yes' : '✗ No'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Equity:</span>
                                    <span className="font-bold">${results.equity.toLocaleString()}</span>
                                </div>
                                <div className="border-t-2 border-gray-300 pt-2 mt-2"></div>
                                <div className="flex justify-between">
                                    <span>Annual Loan Payment:</span>
                                    <span className="font-bold">${results.annualPayment.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Annual Cash Flow:</span>
                                    <span className={`font-bold ${results.cashFlowPositive ? 'text-green-600' : 'text-red-600'}`}>
                                        ${results.annualCashFlow.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Rental Yield:</span>
                                    <span className="font-bold">{results.rentalYield.toFixed(2)}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-purple-600 text-white font-bold text-base rounded-xl active:bg-purple-700">
                    Analyze LRBA
                </button>
            </div>
        </div>
    );
};

// 4. Pension Optimizer Calculator
const PensionOptimizerCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        age: 65,
        superBalance: 800000,
        pensionBalance: 0,
        tbcUsed: 0,
        desiredIncome: 60000
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const tbcCap = FinancialConfig.TRANSFER_BALANCE_CAP;
        const tbcRemaining = tbcCap - inputs.tbcUsed;
        const maxTransfer = Math.min(inputs.superBalance, tbcRemaining);
        const minDrawdown = inputs.pensionBalance * 0.04; // 4% minimum at age 65
        
        setResults({
            tbcCap,
            tbcUsed: inputs.tbcUsed,
            tbcRemaining,
            maxTransfer,
            currentPensionBalance: inputs.pensionBalance,
            minDrawdown,
            taxFreeIncome: inputs.pensionBalance > 0 ? inputs.desiredIncome : 0,
            canTransfer: maxTransfer > 0
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-green-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-green-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Pension Optimizer</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Pension Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Age</label>
                        <input type="number" value={inputs.age}
                            onChange={(e) => setInputs(prev => ({ ...prev, age: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Super Balance ($)</label>
                        <input type="number" value={inputs.superBalance}
                            onChange={(e) => setInputs(prev => ({ ...prev, superBalance: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Current Pension Balance ($)</label>
                        <input type="number" value={inputs.pensionBalance}
                            onChange={(e) => setInputs(prev => ({ ...prev, pensionBalance: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">TBC Already Used ($)</label>
                        <input type="number" value={inputs.tbcUsed}
                            onChange={(e) => setInputs(prev => ({ ...prev, tbcUsed: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className="space-y-3">
                        <div className="rounded-xl p-6 bg-green-50 border-2 border-green-200">
                            <h3 className="font-bold text-gray-900 mb-3">Pension Optimization</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Transfer Balance Cap:</span>
                                    <span className="font-bold">${results.tbcCap.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>TBC Used:</span>
                                    <span className="font-bold">${results.tbcUsed.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>TBC Remaining:</span>
                                    <span className="font-bold text-green-600">${results.tbcRemaining.toLocaleString()}</span>
                                </div>
                                <div className="border-t-2 border-gray-300 pt-2 mt-2"></div>
                                <div className="flex justify-between text-lg">
                                    <span className="font-bold">Max Transfer:</span>
                                    <span className="font-bold text-green-600">${results.maxTransfer.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Min Annual Drawdown:</span>
                                    <span className="font-bold">${results.minDrawdown.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-green-600 text-white font-bold text-base rounded-xl active:bg-green-700">
                    Optimize Pension
                </button>
            </div>
        </div>
    );
};

// 5. Carry Forward Calculator
const CarryForwardCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        totalSuperBalance: 400000,
        concessional2020: 15000,
        concessional2021: 18000,
        concessional2022: 20000,
        concessional2023: 22000,
        concessional2024: 25000,
        plannedContribution: 50000
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const cap = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
        const tsbThreshold = FinancialConfig.CONTRIBUTION_CAPS.TOTAL_BALANCE_THRESHOLD;
        const eligible = inputs.totalSuperBalance < tsbThreshold;
        
        const unused2020 = Math.max(0, cap - inputs.concessional2020);
        const unused2021 = Math.max(0, cap - inputs.concessional2021);
        const unused2022 = Math.max(0, cap - inputs.concessional2022);
        const unused2023 = Math.max(0, cap - inputs.concessional2023);
        const unused2024 = Math.max(0, cap - inputs.concessional2024);
        
        const totalCarryForward = unused2020 + unused2021 + unused2022 + unused2023 + unused2024;
        const totalAvailable = cap + (eligible ? totalCarryForward : 0);
        const canContribute = Math.min(inputs.plannedContribution, totalAvailable);
        const excess = Math.max(0, inputs.plannedContribution - totalAvailable);
        
        setResults({
            cap,
            eligible,
            tsbThreshold,
            totalCarryForward,
            totalAvailable,
            plannedContribution: inputs.plannedContribution,
            canContribute,
            excess,
            compliant: excess === 0
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-indigo-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-indigo-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Carry Forward</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Contribution History</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Total Super Balance ($)</label>
                        <input type="number" value={inputs.totalSuperBalance}
                            onChange={(e) => setInputs(prev => ({ ...prev, totalSuperBalance: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">2020-21 Contributions ($)</label>
                        <input type="number" value={inputs.concessional2020}
                            onChange={(e) => setInputs(prev => ({ ...prev, concessional2020: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">2024-25 Planned ($)</label>
                        <input type="number" value={inputs.plannedContribution}
                            onChange={(e) => setInputs(prev => ({ ...prev, plannedContribution: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className="space-y-3">
                        <div className={`rounded-xl p-6 ${results.eligible ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                            <h3 className="font-bold text-gray-900 mb-3">Carry Forward Analysis</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Eligible for Carry Forward:</span>
                                    <span className={`font-bold ${results.eligible ? 'text-green-600' : 'text-red-600'}`}>
                                        {results.eligible ? '✓ Yes' : '✗ No'}
                                    </span>
                                </div>
                                {!results.eligible && (
                                    <div className="text-xs text-red-600">
                                        TSB ≥ ${results.tsbThreshold.toLocaleString()}
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span>Annual Cap:</span>
                                    <span className="font-bold">${results.cap.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Carry Forward Amount:</span>
                                    <span className="font-bold text-green-600">${results.totalCarryForward.toLocaleString()}</span>
                                </div>
                                <div className="border-t-2 border-gray-300 pt-2 mt-2"></div>
                                <div className="flex justify-between text-lg">
                                    <span className="font-bold">Total Available:</span>
                                    <span className="font-bold text-green-600">${results.totalAvailable.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Can Contribute:</span>
                                    <span className="font-bold">${results.canContribute.toLocaleString()}</span>
                                </div>
                                {results.excess > 0 && (
                                    <div className="flex justify-between text-red-600">
                                        <span>Excess:</span>
                                        <span className="font-bold">${results.excess.toLocaleString()}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-indigo-600 text-white font-bold text-base rounded-xl active:bg-indigo-700">
                    Calculate Carry Forward
                </button>
            </div>
        </div>
    );
};

// 6. Fee Analysis Calculator
const FeeAnalysisCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        superBalance: 500000,
        // SMSF fees
        smsfAdminFees: 2000,
        smsfAuditFees: 800,
        smsfAccountingFees: 1200,
        smsfInvestmentFees: 0.5,
        // Retail/Industry fund fees (for comparison)
        retailAdminFees: 200,
        retailInvestmentFees: 1.2
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const smsfTotalFixed = inputs.smsfAdminFees + inputs.smsfAuditFees + inputs.smsfAccountingFees;
        const smsfTotalVariable = inputs.superBalance * (inputs.smsfInvestmentFees / 100);
        const smsfTotalAnnual = smsfTotalFixed + smsfTotalVariable;
        
        const retailTotalFixed = inputs.retailAdminFees;
        const retailTotalVariable = inputs.superBalance * (inputs.retailInvestmentFees / 100);
        const retailTotalAnnual = retailTotalFixed + retailTotalVariable;
        
        const saving = retailTotalAnnual - smsfTotalAnnual;
        const smsfCheaper = saving > 0;
        
        setResults({
            smsf: {
                fixed: smsfTotalFixed,
                variable: smsfTotalVariable,
                total: smsfTotalAnnual,
                percentage: (smsfTotalAnnual / inputs.superBalance) * 100
            },
            retail: {
                fixed: retailTotalFixed,
                variable: retailTotalVariable,
                total: retailTotalAnnual,
                percentage: (retailTotalAnnual / inputs.superBalance) * 100
            },
            comparison: {
                saving: Math.abs(saving),
                smsfCheaper,
                savingPercentage: (Math.abs(saving) / retailTotalAnnual) * 100
            }
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-orange-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-orange-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Fee Analysis</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Fee Comparison</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Super Balance ($)</label>
                        <input type="number" value={inputs.superBalance}
                            onChange={(e) => setInputs(prev => ({ ...prev, superBalance: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <h3 className="font-bold text-gray-800 mt-6">SMSF Fees</h3>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Fees ($/year)</label>
                        <input type="number" value={inputs.smsfAdminFees}
                            onChange={(e) => setInputs(prev => ({ ...prev, smsfAdminFees: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Investment Fees (%/year)</label>
                        <input type="number" step="0.1" value={inputs.smsfInvestmentFees}
                            onChange={(e) => setInputs(prev => ({ ...prev, smsfInvestmentFees: parseFloat(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className="space-y-3">
                        <div className="rounded-xl p-6 bg-blue-50 border-2 border-blue-200">
                            <h3 className="font-bold text-gray-900 mb-3">SMSF Annual Fees</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Total Annual:</span>
                                    <span className="font-bold">${results.smsf.total.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-600">
                                    <span>As % of balance:</span>
                                    <span>{results.smsf.percentage.toFixed(2)}%</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="rounded-xl p-6 bg-gray-50 border-2 border-gray-200">
                            <h3 className="font-bold text-gray-900 mb-3">Retail Fund Fees</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Total Annual:</span>
                                    <span className="font-bold">${results.retail.total.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-xs text-gray-600">
                                    <span>As % of balance:</span>
                                    <span>{results.retail.percentage.toFixed(2)}%</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className={`rounded-xl p-6 ${results.comparison.smsfCheaper ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                            <h3 className="font-bold text-gray-900 mb-3">Comparison</h3>
                            <div className="text-lg font-bold text-center">
                                {results.comparison.smsfCheaper ? (
                                    <span className="text-green-600">SMSF Saves ${results.comparison.saving.toLocaleString()}/year</span>
                                ) : (
                                    <span className="text-red-600">SMSF Costs ${results.comparison.saving.toLocaleString()}/year more</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-orange-600 text-white font-bold text-base rounded-xl active:bg-orange-700">
                    Compare Fees
                </button>
            </div>
        </div>
    );
};

// 7. Cash Flow Calculator
const CashFlowCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        pensionDrawdown: 50000,
        rentalIncome: 24000,
        dividends: 15000,
        interest: 2000,
        auditFees: 800,
        adminFees: 2000,
        insurancePremiums: 3000,
        loanRepayments: 30000
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const totalIncome = inputs.pensionDrawdown + inputs.rentalIncome + inputs.dividends + inputs.interest;
        const totalExpenses = inputs.auditFees + inputs.adminFees + inputs.insurancePremiums + inputs.loanRepayments;
        const netCashFlow = totalIncome - totalExpenses;
        const monthlyCashFlow = netCashFlow / 12;
        
        setResults({
            totalIncome,
            totalExpenses,
            netCashFlow,
            monthlyCashFlow,
            cashFlowPositive: netCashFlow >= 0
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-teal-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-teal-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Cash Flow</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Annual Cash Flow</h2>
                    
                    <h3 className="font-bold text-green-800 text-sm">Income</h3>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Pension Drawdown ($)</label>
                        <input type="number" value={inputs.pensionDrawdown}
                            onChange={(e) => setInputs(prev => ({ ...prev, pensionDrawdown: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Rental Income ($)</label>
                        <input type="number" value={inputs.rentalIncome}
                            onChange={(e) => setInputs(prev => ({ ...prev, rentalIncome: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <h3 className="font-bold text-red-800 text-sm mt-6">Expenses</h3>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Fees ($)</label>
                        <input type="number" value={inputs.adminFees}
                            onChange={(e) => setInputs(prev => ({ ...prev, adminFees: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Loan Repayments ($)</label>
                        <input type="number" value={inputs.loanRepayments}
                            onChange={(e) => setInputs(prev => ({ ...prev, loanRepayments: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className="space-y-3">
                        <div className={`rounded-xl p-6 ${results.cashFlowPositive ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                            <h3 className="font-bold text-gray-900 mb-3">Cash Flow Analysis</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>Total Income:</span>
                                    <span className="font-bold text-green-600">${results.totalIncome.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Total Expenses:</span>
                                    <span className="font-bold text-red-600">${results.totalExpenses.toLocaleString()}</span>
                                </div>
                                <div className="border-t-2 border-gray-300 pt-2 mt-2"></div>
                                <div className="flex justify-between text-lg">
                                    <span className="font-bold">Net Cash Flow:</span>
                                    <span className={`font-bold ${results.cashFlowPositive ? 'text-green-600' : 'text-red-600'}`}>
                                        ${results.netCashFlow.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span>Monthly:</span>
                                    <span className="font-bold">${results.monthlyCashFlow.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-teal-600 text-white font-bold text-base rounded-xl active:bg-teal-700">
                    Analyze Cash Flow
                </button>
            </div>
        </div>
    );
};

// 8. Compliance Check Calculator
const ComplianceCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        totalAssets: 1000000,
        inHouseAssets: 30000,
        relatedPartyLoans: 20000,
        propertyValue: 500000,
        loanAmount: 400000,
        membersCount: 2,
        hasInvestmentStrategy: true,
        hasDeathBenefitNominations: true
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const inHouseMax = inputs.totalAssets * 0.05;
        const inHouseTotal = inputs.inHouseAssets + inputs.relatedPartyLoans;
        const inHouseCompliant = inHouseTotal <= inHouseMax;
        
        const lvr = inputs.propertyValue > 0 ? (inputs.loanAmount / inputs.propertyValue) * 100 : 0;
        const maxLvrPct = Math.round(FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TO_VALUE * 100);
        const lrbaCompliant = lvr <= maxLvrPct;
        
        const solePurpose = inputs.membersCount > 0;
        const investmentStrategyCompliant = inputs.hasInvestmentStrategy;
        const nominationsCompliant = inputs.hasDeathBenefitNominations;
        
        const totalChecks = 5;
        const passedChecks = [inHouseCompliant, lrbaCompliant, solePurpose, investmentStrategyCompliant, nominationsCompliant].filter(x => x).length;
        
        setResults({
            inHouse: { compliant: inHouseCompliant, current: inHouseTotal, max: inHouseMax },
            lrba: { compliant: lrbaCompliant, lvr },
            solePurpose: { compliant: solePurpose },
            investmentStrategy: { compliant: investmentStrategyCompliant },
            nominations: { compliant: nominationsCompliant },
            overallCompliance: (passedChecks / totalChecks) * 100,
            allCompliant: passedChecks === totalChecks
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-green-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-green-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Compliance Check</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">SMSF Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Total Assets ($)</label>
                        <input type="number" value={inputs.totalAssets}
                            onChange={(e) => setInputs(prev => ({ ...prev, totalAssets: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">In-House Assets ($)</label>
                        <input type="number" value={inputs.inHouseAssets}
                            onChange={(e) => setInputs(prev => ({ ...prev, inHouseAssets: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                        <input type="checkbox" checked={inputs.hasInvestmentStrategy}
                            onChange={(e) => setInputs(prev => ({ ...prev, hasInvestmentStrategy: e.target.checked }))}
                            className="w-6 h-6" />
                        <label className="text-sm font-medium">Has Investment Strategy?</label>
                    </div>
                </div>
                
                {results && (
                    <div className={`rounded-xl p-6 ${results.allCompliant ? 'bg-green-50 border-2 border-green-200' : 'bg-yellow-50 border-2 border-yellow-200'}`}>
                        <h3 className="font-bold text-gray-900 mb-3">Compliance Status</h3>
                        <div className="text-center mb-4">
                            <div className="text-4xl font-bold">{results.overallCompliance.toFixed(0)}%</div>
                            <div className="text-sm text-gray-600">Overall Compliance</div>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>In-House Assets (≤5%):</span>
                                <span className={`font-bold ${results.inHouse.compliant ? 'text-green-600' : 'text-red-600'}`}>
                                    {results.inHouse.compliant ? '✓' : '✗'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span>LRBA Compliance (≤{Math.round(SMSF_RATES_CONFIG.lrbaLimits.maxLoanToValueRatio * 100)}%):</span>
                                <span className={`font-bold ${results.lrba.compliant ? 'text-green-600' : 'text-red-600'}`}>
                                    {results.lrba.compliant ? '✓' : '✗'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span>Investment Strategy:</span>
                                <span className={`font-bold ${results.investmentStrategy.compliant ? 'text-green-600' : 'text-red-600'}`}>
                                    {results.investmentStrategy.compliant ? '✓' : '✗'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-green-600 text-white font-bold text-base rounded-xl active:bg-green-700">
                    Check Compliance
                </button>
            </div>
        </div>
    );
};

// 9. TBC Tracking Calculator
const TBCTrackingCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        currentTBC: 1900000,
        pensionBalance: 800000,
        plannedTransfer: 100000,
        age: 65
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const cap = inputs.currentTBC;
        const used = inputs.pensionBalance;
        const remaining = cap - used;
        const afterTransfer = used + inputs.plannedTransfer;
        const remainingAfterTransfer = cap - afterTransfer;
        const canTransfer = inputs.plannedTransfer <= remaining;
        
        setResults({
            cap,
            used,
            remaining,
            plannedTransfer: inputs.plannedTransfer,
            afterTransfer,
            remainingAfterTransfer,
            canTransfer,
            utilizationBefore: (used / cap) * 100,
            utilizationAfter: (afterTransfer / cap) * 100
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-blue-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-blue-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">TBC Tracking</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Transfer Balance Cap</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Current TBC ($)</label>
                        <input type="number" value={inputs.currentTBC}
                            onChange={(e) => setInputs(prev => ({ ...prev, currentTBC: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Current Pension Balance ($)</label>
                        <input type="number" value={inputs.pensionBalance}
                            onChange={(e) => setInputs(prev => ({ ...prev, pensionBalance: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Planned Transfer ($)</label>
                        <input type="number" value={inputs.plannedTransfer}
                            onChange={(e) => setInputs(prev => ({ ...prev, plannedTransfer: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className="space-y-3">
                        <div className={`rounded-xl p-6 ${results.canTransfer ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                            <h3 className="font-bold text-gray-900 mb-3">TBC Status</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span>TBC Cap:</span>
                                    <span className="font-bold">${results.cap.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Currently Used:</span>
                                    <span className="font-bold">${results.used.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Remaining:</span>
                                    <span className="font-bold text-green-600">${results.remaining.toLocaleString()}</span>
                                </div>
                                <div className="border-t-2 border-gray-300 pt-2 mt-2"></div>
                                <div className="flex justify-between">
                                    <span>After Planned Transfer:</span>
                                    <span className={`font-bold ${results.canTransfer ? 'text-green-600' : 'text-red-600'}`}>
                                        ${results.afterTransfer.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span>Utilization:</span>
                                    <span>{results.utilizationAfter.toFixed(1)}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-blue-600 text-white font-bold text-base rounded-xl active:bg-blue-700">
                    Check TBC
                </button>
            </div>
        </div>
    );
};

// 10. Investment Strategy Calculator
const InvestmentStrategyCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({
        age: 60,
        riskTolerance: 'moderate',
        retirementTimeframe: 10,
        currentAllocation: { shares: 60, bonds: 30, cash: 10 }
    });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        let recommended = { shares: 50, bonds: 35, cash: 15 };
        
        if (inputs.riskTolerance === 'conservative') {
            recommended = { shares: 30, bonds: 50, cash: 20 };
        } else if (inputs.riskTolerance === 'growth') {
            recommended = { shares: 70, bonds: 20, cash: 10 };
        }
        
        if (inputs.age >= 70) {
            recommended.cash += 10;
            recommended.shares -= 10;
        }
        
        setResults({
            recommended,
            current: inputs.currentAllocation,
            rebalanceNeeded: Math.abs(inputs.currentAllocation.shares - recommended.shares) > 10
        });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-purple-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-purple-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Investment Strategy</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Strategy Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Age</label>
                        <input type="number" value={inputs.age}
                            onChange={(e) => setInputs(prev => ({ ...prev, age: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Risk Tolerance</label>
                        <select value={inputs.riskTolerance}
                            onChange={(e) => setInputs(prev => ({ ...prev, riskTolerance: e.target.value }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl">
                            <option value="conservative">Conservative</option>
                            <option value="moderate">Moderate</option>
                            <option value="growth">Growth</option>
                        </select>
                    </div>
                </div>
                
                {results && (
                    <div className="rounded-xl p-6 bg-purple-50 border-2 border-purple-200">
                        <h3 className="font-bold text-gray-900 mb-3">Recommended Allocation</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Shares:</span>
                                <span className="font-bold">{results.recommended.shares}%</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Bonds:</span>
                                <span className="font-bold">{results.recommended.bonds}%</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Cash:</span>
                                <span className="font-bold">{results.recommended.cash}%</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-purple-600 text-white font-bold text-base rounded-xl active:bg-purple-700">
                    Generate Strategy
                </button>
            </div>
        </div>
    );
};

// 11. Downsizer Calculator
const DownsizerCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({ age: 65, saleProceeds: 600000, ownershipYears: 15 });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const eligible = inputs.age >= 55 && inputs.ownershipYears >= 10;
        const maxContribution = Math.min(inputs.saleProceeds, 300000);
        setResults({ eligible, maxContribution, age: inputs.age });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-sky-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-sky-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Downsizer</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Downsizer Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Age</label>
                        <input type="number" value={inputs.age}
                            onChange={(e) => setInputs(prev => ({ ...prev, age: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Home Sale Proceeds ($)</label>
                        <input type="number" value={inputs.saleProceeds}
                            onChange={(e) => setInputs(prev => ({ ...prev, saleProceeds: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Years Owned</label>
                        <input type="number" value={inputs.ownershipYears}
                            onChange={(e) => setInputs(prev => ({ ...prev, ownershipYears: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className={`rounded-xl p-6 ${results.eligible ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                        <h3 className="font-bold text-gray-900 mb-3">Downsizer Eligibility</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Eligible:</span>
                                <span className={`font-bold ${results.eligible ? 'text-green-600' : 'text-red-600'}`}>
                                    {results.eligible ? '✓ Yes' : '✗ No'}
                                </span>
                            </div>
                            {results.eligible && (
                                <div className="flex justify-between text-lg mt-3">
                                    <span className="font-bold">Max Contribution:</span>
                                    <span className="font-bold text-green-600">${results.maxContribution.toLocaleString()}</span>
                                </div>
                            )}
                            {!results.eligible && (
                                <div className="text-xs text-red-600 mt-2">
                                    Requirements: Age 55+ and owned home for 10+ years
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-sky-600 text-white font-bold text-base rounded-xl active:bg-sky-700">
                    Check Eligibility
                </button>
            </div>
        </div>
    );
};

// 12. Gov Co-Contribution Calculator
const CoContributionCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({ income: 45000, afterTaxContribution: 1000 });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const maxIncome = 60400;
        const minIncome = 45400;
        const eligible = inputs.income <= maxIncome;
        let coContribution = 0;
        
        if (eligible && inputs.income <= minIncome) {
            coContribution = Math.min(inputs.afterTaxContribution * 0.5, 500);
        } else if (eligible) {
            const reduction = (inputs.income - minIncome) / (maxIncome - minIncome);
            coContribution = Math.max(0, Math.min(inputs.afterTaxContribution * 0.5, 500) * (1 - reduction));
        }
        
        setResults({ eligible, coContribution, maxPossible: 500 });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-green-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-green-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Gov Co-Contribution</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Eligibility Check</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Income ($)</label>
                        <input type="number" value={inputs.income}
                            onChange={(e) => setInputs(prev => ({ ...prev, income: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">After-Tax Contribution ($)</label>
                        <input type="number" value={inputs.afterTaxContribution}
                            onChange={(e) => setInputs(prev => ({ ...prev, afterTaxContribution: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className={`rounded-xl p-6 ${results.eligible ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                        <h3 className="font-bold text-gray-900 mb-3">Co-Contribution Result</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Eligible:</span>
                                <span className={`font-bold ${results.eligible ? 'text-green-600' : 'text-red-600'}`}>
                                    {results.eligible ? '✓ Yes' : '✗ No'}
                                </span>
                            </div>
                            {results.eligible && (
                                <>
                                    <div className="flex justify-between text-lg mt-3">
                                        <span className="font-bold">Gov Co-Contribution:</span>
                                        <span className="font-bold text-green-600">${results.coContribution.toLocaleString()}</span>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-2">
                                        Maximum possible: ${results.maxPossible.toLocaleString()}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-green-600 text-white font-bold text-base rounded-xl active:bg-green-700">
                    Calculate
                </button>
            </div>
        </div>
    );
};

// 13. Tax Harvesting Calculator
const TaxHarvestingCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({ capitalGains: 50000, capitalLosses: 20000, fundTaxRate: 15 });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const netGain = Math.max(0, inputs.capitalGains - inputs.capitalLosses);
        const taxSaved = inputs.capitalLosses * (inputs.fundTaxRate / 100);
        setResults({ netGain, taxSaved });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-red-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-red-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Tax Harvesting</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Capital Gains/Losses</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Capital Gains ($)</label>
                        <input type="number" value={inputs.capitalGains}
                            onChange={(e) => setInputs(prev => ({ ...prev, capitalGains: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Capital Losses ($)</label>
                        <input type="number" value={inputs.capitalLosses}
                            onChange={(e) => setInputs(prev => ({ ...prev, capitalLosses: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Fund Tax Rate (%)</label>
                        <input type="number" value={inputs.fundTaxRate}
                            onChange={(e) => setInputs(prev => ({ ...prev, fundTaxRate: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className="rounded-xl p-6 bg-green-50 border-2 border-green-200">
                        <h3 className="font-bold text-gray-900 mb-3">Tax Harvesting Result</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Net Capital Gain:</span>
                                <span className="font-bold">${results.netGain.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-lg mt-3">
                                <span className="font-bold">Tax Saved:</span>
                                <span className="font-bold text-green-600">${results.taxSaved.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-red-600 text-white font-bold text-base rounded-xl active:bg-red-700">
                    Calculate
                </button>
            </div>
        </div>
    );
};

// 14. Work Test Calculator
const WorkTestCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({ age: 68, hoursWorked: 40, employmentDays: 30 });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const needsWorkTest = inputs.age >= 67 && inputs.age < 75;
        const meetsTest = inputs.hoursWorked >= 40 && inputs.employmentDays >= 30;
        const canContribute = !needsWorkTest || meetsTest;
        setResults({ needsWorkTest, meetsTest, canContribute });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-yellow-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-yellow-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Work Test</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Work Test Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Age</label>
                        <input type="number" value={inputs.age}
                            onChange={(e) => setInputs(prev => ({ ...prev, age: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Hours Worked (last year)</label>
                        <input type="number" value={inputs.hoursWorked}
                            onChange={(e) => setInputs(prev => ({ ...prev, hoursWorked: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Employment Days</label>
                        <input type="number" value={inputs.employmentDays}
                            onChange={(e) => setInputs(prev => ({ ...prev, employmentDays: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                </div>
                
                {results && (
                    <div className={`rounded-xl p-6 ${results.canContribute ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                        <h3 className="font-bold text-gray-900 mb-3">Work Test Result</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Needs Work Test:</span>
                                <span className="font-bold">{results.needsWorkTest ? 'Yes (age 67-74)' : 'No'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Meets Test:</span>
                                <span className={`font-bold ${results.meetsTest ? 'text-green-600' : 'text-red-600'}`}>
                                    {results.meetsTest ? '✓ Yes' : '✗ No'}
                                </span>
                            </div>
                            <div className="border-t-2 border-gray-300 pt-2 mt-2"></div>
                            <div className="flex justify-between text-lg">
                                <span className="font-bold">Can Contribute:</span>
                                <span className={`font-bold ${results.canContribute ? 'text-green-600' : 'text-red-600'}`}>
                                    {results.canContribute ? '✓ Yes' : '✗ No'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-yellow-600 text-white font-bold text-base rounded-xl active:bg-yellow-700">
                    Check Work Test
                </button>
            </div>
        </div>
    );
};

// 15. Spouse Splitting Calculator
const SpouseSplitCalculator = ({ onBack }) => {
    const [inputs, setInputs] = useState({ yourContributions: 25000, splitPercentage: 85 });
    const [results, setResults] = useState(null);
    
    const handleCalculate = () => {
        const maxSplit = inputs.yourContributions * 0.85;
        const plannedSplit = inputs.yourContributions * (inputs.splitPercentage / 100);
        const actualSplit = Math.min(plannedSplit, maxSplit);
        const canSplit = actualSplit > 0;
        setResults({ maxSplit, actualSplit, canSplit });
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '80px' }}>
            <div className="bg-pink-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-pink-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">Spouse Splitting</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4 space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-lg space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 mb-4">Splitting Details</h2>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Contributions ($)</label>
                        <input type="number" value={inputs.yourContributions}
                            onChange={(e) => setInputs(prev => ({ ...prev, yourContributions: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Split Percentage (%)</label>
                        <input type="number" value={inputs.splitPercentage}
                            onChange={(e) => setInputs(prev => ({ ...prev, splitPercentage: parseInt(e.target.value) || 0 }))}
                            className="w-full px-4 py-3 text-lg font-bold border-2 border-gray-300 rounded-xl" />
                        <p className="text-xs text-gray-600 mt-2">Maximum: 85%</p>
                    </div>
                </div>
                
                {results && (
                    <div className="rounded-xl p-6 bg-pink-50 border-2 border-pink-200">
                        <h3 className="font-bold text-gray-900 mb-3">Splitting Result</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span>Max Split (85%):</span>
                                <span className="font-bold">${results.maxSplit.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-lg mt-3">
                                <span className="font-bold">Actual Split:</span>
                                <span className="font-bold text-pink-600">${results.actualSplit.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Can Split:</span>
                                <span className={`font-bold ${results.canSplit ? 'text-green-600' : 'text-red-600'}`}>
                                    {results.canSplit ? '✓ Yes' : '✗ No'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
                <button onClick={handleCalculate}
                    className="w-full py-4 bg-pink-600 text-white font-bold text-base rounded-xl active:bg-pink-700">
                    Calculate Split
                </button>
            </div>
        </div>
    );
};

// Coming Soon Template
const ComingSoonTool = ({ tool, onBack }) => {
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: '20px' }}>
            <div className="bg-gray-600 text-white px-3 py-3" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-gray-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-base font-bold">{tool.name}</h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            <div className="p-4">
                <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                    <div className="text-6xl mb-4">{tool.icon}</div>
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Coming Soon</h2>
                    <p className="text-gray-700 mb-6">
                        This tool is currently under development and will be available in a future update.
                    </p>
                    <a
                        href="/app.html"
                        className="inline-block px-6 py-3 bg-gray-700 text-white font-semibold rounded-lg active:bg-gray-800"
                    >
                        Use Desktop Version
                    </a>
                </div>
            </div>
        </div>
    );
};

export default SMSFLiteMobile;

